import { Badge } from "./badge";
import { Button } from "./button";
import { Card, CardHeader, CardFooter } from "./card";
import { Avatar, AvatarImage, AvatarFallback } from "./avatar";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "./tooltip";
import { CalendarClock, CalendarMinus, CalendarX, CalendarCheck, Trash, Video } from "lucide-react";
import { ComponentProps } from "react";
import { cn } from "../lib/utils";

interface Assignee {
  src?: string;
  alt?: string;
  name?: string;
}

interface EventParticipant {
  src?: string;
  alt?: string;
  name?: string;
  isHost?: boolean;
}

const getAssigneeKey = (assignee: Assignee, index?: number) =>
  `${assignee.name ?? ""}${assignee.alt ?? ""}${assignee.src ?? ""}${index ?? ""}`.trim();

function formatParticipantsTooltip(participants: EventParticipant[]): string {
  const hosts = participants.filter((p) => p.isHost);
  const attendees = participants.filter((p) => !p.isHost);

  const hostNames = hosts.map((h) => h.name || h.alt || "Host").join(", ");
  const attendeeNames = attendees.map((a) => a.name || a.alt || "Attendee").join(", ");

  const parts = [];
  if (hostNames) parts.push(`Host: ${hostNames}`);
  if (attendeeNames) parts.push(`Attendees: ${attendeeNames}`);

  return parts.join(", ");
}

function formatEventDate(eventDate?: Date | string): string {
  if (!eventDate) return "";

  const date = typeof eventDate === "string" ? new Date(eventDate) : eventDate;
  const today = new Date();

  // Check if it's today by comparing year, month, and day
  const isToday =
    date.getFullYear() === today.getFullYear() &&
    date.getMonth() === today.getMonth() &&
    date.getDate() === today.getDate();

  if (isToday) {
    return "Today";
  }

  // Format as MM/DD/YYYY
  return date.toLocaleDateString("en-US", {
    month: "2-digit",
    day: "2-digit",
    year: "numeric",
  });
}

function getInitials(name?: string): string {
  if (!name) return "?";
  return name
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase())
    .join("")
    .slice(0, 2);
}

export interface DashboardCardProps extends ComponentProps<typeof Card> {
  status?: "default" | "secondary" | "destructive" | "outline";
  statusLabel?: string;
  title?: string;
  subtitle?: string;
  subtitleSecondary?: string;
  assignees?: Assignee[] | null;
  description?: string;
  dueDate?: string;
  priority?: string;
  className?: string;
  timestamp?: Date | string;
  variant?: "default" | "event";
  eventTime?: string;
  eventDate?: Date | string;
  participants?: EventParticipant[] | null;
  eventActions?: {
    onCancel?: () => void;
    onNoShow?: () => void;
    onComplete?: () => void;
    onVideoCall?: () => void;
    videoCallLabel?: string;
  };
  showUnreadDot?: boolean;
  onDelete?: () => void;
  /** Position of the delete button. Defaults to "title" for backwards compatibility */
  deletePosition?: "title" | "status";
  /** Custom CSS classes to override default badge styling */
  customStatusStyles?: string;
  /** Custom CSS classes for priority badge styling */
  customPriorityStyles?: string;
  /** Ticket ID for navigation */
  ticketId?: string;
}

export function DashboardCard({
  status = "default",
  statusLabel = "In progress",
  title = "Ticket title",
  subtitle,
  subtitleSecondary,
  assignees = [
    { src: "https://github.com/shadcn.png", alt: "User" },
    { src: "https://github.com/shadcn.png", alt: "User" },
    { src: "https://github.com/shadcn.png", alt: "User" },
    { src: "https://github.com/shadcn.png", alt: "User" },
    { src: "https://github.com/shadcn.png", alt: "User" },
  ],
  description = "This is a truncated card description to display the description of the ticket in a way that is glancable",
  dueDate = "Today",
  priority = "Low priority",
  className = "",
  timestamp,
  variant = "default",
  eventTime,
  eventDate,
  participants,
  eventActions,
  showUnreadDot: _showUnreadDot = true,
  onDelete,
  deletePosition = "title",
  customStatusStyles,
  customPriorityStyles,
  ...rest
}: DashboardCardProps) {
  // Default card variant (existing behavior) - disable status row above title for cleaner layout
  const shouldShowStatusLabelRow = false;

  if (variant === "event") {
    return (
      <Card
        className={`rounded-xl border shadow-sm hover:shadow-md transition-shadow cursor-pointer pb-0 ${className}`}
        {...rest}
      >
        <CardHeader className="px-6 pt-0 pb-0 space-y-4">
          <div className="flex items-start justify-between">
            <div className="flex flex-col">
              <h3 className="text-lg font-semibold text-card-foreground">{title}</h3>
              {(eventDate || eventTime) && (
                <p className="text-sm text-muted-foreground mt-1">
                  {eventDate && <span className="text-foreground">{formatEventDate(eventDate)}</span>}
                  {eventDate && eventTime && <span className="ml-2"> </span>}
                  {eventTime && <span>{eventTime}</span>}
                </p>
              )}
            </div>
            {shouldShowStatusLabelRow && (participants || assignees) && (
              <TooltipProvider>
                {participants ? (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <div className="flex -space-x-2">
                        {participants.map((participant, index) => (
                          <Avatar
                            key={`${participant.name ?? ""}${participant.alt ?? ""}${participant.src ?? ""}${index}`}
                            className="h-5 w-5 border border-card bg-background"
                          >
                            <AvatarImage src={participant.src} alt={participant.alt} />
                            <AvatarFallback className="text-xs">
                              {getInitials(participant.name || participant.alt)}
                            </AvatarFallback>
                          </Avatar>
                        ))}
                      </div>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>{formatParticipantsTooltip(participants)}</p>
                    </TooltipContent>
                  </Tooltip>
                ) : (
                  <div className="flex -space-x-2">
                    {assignees!.map((assignee, index) => (
                      <Tooltip key={getAssigneeKey(assignee, index)}>
                        <TooltipTrigger asChild>
                          <Avatar className="h-8 w-8 border border-card bg-background">
                            <AvatarImage src={assignee.src} alt={assignee.alt} />
                            <AvatarFallback className="text-xs">
                              {getInitials(assignee.name || assignee.alt)}
                            </AvatarFallback>
                          </Avatar>
                        </TooltipTrigger>
                        <TooltipContent>
                          <p>{assignee.name || assignee.alt || "Attendee"}</p>
                        </TooltipContent>
                      </Tooltip>
                    ))}
                  </div>
                )}
              </TooltipProvider>
            )}
          </div>
        </CardHeader>

        <CardFooter className="border-t">
          {eventActions && (
            <TooltipProvider>
              <div className="flex items-center w-full py-4">
                {eventActions.onVideoCall && (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button
                        onClick={eventActions.onVideoCall}
                        className="p-2 hover:bg-muted rounded transition-colors"
                      >
                        <Video className="h-4 w-4 text-muted-foreground hover:text-foreground" />
                      </button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>{eventActions.videoCallLabel ?? "Join Video Call"}</p>
                    </TooltipContent>
                  </Tooltip>
                )}

                <Tooltip>
                  <TooltipTrigger asChild>
                    <button onClick={eventActions.onCancel} className="p-2 hover:bg-muted rounded transition-colors">
                      <CalendarMinus className="h-4 w-4 text-muted-foreground hover:text-foreground" />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Cancel Event</p>
                  </TooltipContent>
                </Tooltip>

                <Tooltip>
                  <TooltipTrigger asChild>
                    <button onClick={eventActions.onNoShow} className="p-2 hover:bg-muted rounded transition-colors">
                      <CalendarX className="h-4 w-4 text-muted-foreground hover:text-foreground" />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>No Show</p>
                  </TooltipContent>
                </Tooltip>

                <Tooltip>
                  <TooltipTrigger asChild>
                    <button onClick={eventActions.onComplete} className="p-2 hover:bg-muted rounded transition-colors">
                      <CalendarCheck className="h-4 w-4 text-muted-foreground hover:text-foreground" />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Completed</p>
                  </TooltipContent>
                </Tooltip>
              </div>
            </TooltipProvider>
          )}
        </CardFooter>
      </Card>
    );
  }

  return (
    <Card
      className={`rounded-xl border shadow-sm hover:shadow-md transition-shadow cursor-pointer ${className}`}
      {...rest}
    >
      <CardHeader className="px-6 pt-0 pb-0 space-y-4">
        <div className="flex flex-col space-y-2">
          {shouldShowStatusLabelRow ? (
            <div className="flex items-center justify-between">
              {customStatusStyles ? (
                <span
                  className={cn(
                    "inline-flex items-center justify-center rounded-md border px-2 py-0.5 text-xs font-medium w-fit whitespace-nowrap shrink-0",
                    customStatusStyles
                  )}
                >
                  {statusLabel}
                </span>
              ) : (
                <Badge className="w-fit" variant={status}>
                  {statusLabel}
                </Badge>
              )}
              <div className="flex items-center gap-2">
                {onDelete && deletePosition === "status" && (
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-9 w-9 p-0"
                          onClick={(e) => {
                            e.stopPropagation();
                            onDelete();
                          }}
                        >
                          <Trash className="h-4 w-4 text-foreground" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>Delete</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                )}
                {assignees && assignees.length > 0 ? (
                  <TooltipProvider>
                    <div className="flex -space-x-2">
                      {assignees.map((assignee, index) => (
                        <Tooltip key={getAssigneeKey(assignee, index)}>
                          <TooltipTrigger asChild>
                            <Avatar className="h-8 w-8 border border-card bg-background">
                              <AvatarImage src={assignee.src} alt={assignee.alt} />
                              <AvatarFallback className="text-xs">
                                {getInitials(assignee.name || assignee.alt)}
                              </AvatarFallback>
                            </Avatar>
                          </TooltipTrigger>
                          <TooltipContent>
                            <p>{assignee.name || assignee.alt || "User"}</p>
                          </TooltipContent>
                        </Tooltip>
                      ))}
                    </div>
                  </TooltipProvider>
                ) : !onDelete || deletePosition !== "status" ? (
                  <span className="text-sm text-muted-foreground">Not assigned</span>
                ) : null}
              </div>
            </div>
          ) : null}

          <div className="flex items-center justify-between">
            <div className="flex items-center flex-1">
              <h3 className="text-lg font-semibold text-card-foreground">{title}</h3>
            </div>
            <div className="flex items-center">
              {assignees && assignees.length > 0 && (
                <TooltipProvider>
                  <div className="flex -space-x-2">
                    {assignees.map((assignee, index) => (
                      <Tooltip key={getAssigneeKey(assignee, index)}>
                        <TooltipTrigger asChild>
                          <Avatar className="h-8 w-8 border border-card bg-background">
                            <AvatarImage src={assignee.src} alt={assignee.alt} />
                            <AvatarFallback className="text-xs">
                              {getInitials(assignee.name || assignee.alt)}
                            </AvatarFallback>
                          </Avatar>
                        </TooltipTrigger>
                        <TooltipContent>
                          <p>{assignee.name || assignee.alt || "User"}</p>
                        </TooltipContent>
                      </Tooltip>
                    ))}
                  </div>
                </TooltipProvider>
              )}
            </div>
            {onDelete && shouldShowStatusLabelRow && deletePosition === "title" && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-9 w-9 p-0"
                      onClick={(e) => {
                        e.stopPropagation();
                        onDelete();
                      }}
                    >
                      <Trash className="h-4 w-4 text-foreground" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Delete</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
            <div className="flex items-center gap-2">
              {onDelete && !shouldShowStatusLabelRow && (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-9 w-9 p-0"
                        onClick={(e) => {
                          e.stopPropagation();
                          onDelete();
                        }}
                      >
                        <Trash className="h-4 w-4 text-foreground" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Delete</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}
            </div>
          </div>

          {subtitle || subtitleSecondary ? (
            <div className="flex items-center">
              {subtitle && <span className="text-sm text-foreground pr-2">{subtitle}</span>}
              {subtitleSecondary && <span className="text-sm text-muted-foreground">{subtitleSecondary}</span>}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground line-clamp-1">{description}</p>
          )}
        </div>
      </CardHeader>

      {((dueDate && dueDate.trim()) || (priority && priority.trim()) || timestamp !== undefined) && !onDelete && (
        <CardFooter className="px-6 pt-6 pb-0 flex items-center justify-between border-t">
          <div className="flex items-center space-x-3">
            <div className="flex items-center space-x-1">
              <CalendarClock className="h-4 w-4 text-foreground" />
              <span className="text-sm text-foreground">{dueDate}</span>
            </div>
            {(statusLabel?.trim() || priority?.trim()) && (
              <div className="flex items-center space-x-2">
                {statusLabel?.trim() && (
                  <>
                    {customStatusStyles ? (
                      <span
                        className={cn(
                          "inline-flex items-center justify-center rounded-md border px-2 py-0.5 text-xs font-medium w-fit whitespace-nowrap shrink-0",
                          customStatusStyles
                        )}
                      >
                        {statusLabel}
                      </span>
                    ) : (
                      <Badge className="w-fit" variant={status}>
                        {statusLabel}
                      </Badge>
                    )}
                  </>
                )}
                {!!priority?.trim() && (
                  <>
                    {customPriorityStyles ? (
                      <span
                        className={cn(
                          "inline-flex items-center justify-center rounded-md border px-2 py-0.5 text-xs font-medium w-fit whitespace-nowrap shrink-0",
                          customPriorityStyles
                        )}
                      >
                        {priority}
                      </span>
                    ) : (
                      <Badge className="w-fit" variant="outline">
                        {priority}
                      </Badge>
                    )}
                  </>
                )}
              </div>
            )}
          </div>
        </CardFooter>
      )}
    </Card>
  );
}
